import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const PrivacyPage = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <Navbar />
      
      <main className="vf-section">
        <div className="vf-container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="vf-heading-1 text-[#1e3a5f] mb-8">
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
            </h1>

            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 mb-8">
                {language === 'es' 
                  ? 'Última actualización: Enero 2025'
                  : 'Last updated: January 2025'}
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  1. {language === 'es' ? 'Información que Recopilamos' : 'Information We Collect'}
                </h2>
                <p className="text-slate-600 mb-4">
                  {language === 'es'
                    ? 'Recopilamos información que nos proporcionas directamente:'
                    : 'We collect information you provide directly:'}
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-2">
                  <li>{language === 'es' ? 'Datos de registro (nombre, email, NIF/CIF)' : 'Registration data (name, email, NIF/CIF)'}</li>
                  <li>{language === 'es' ? 'Información de facturación' : 'Billing information'}</li>
                  <li>{language === 'es' ? 'Datos de productos e inventario' : 'Product and inventory data'}</li>
                  <li>{language === 'es' ? 'Historial de transacciones' : 'Transaction history'}</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  2. {language === 'es' ? 'Uso de la Información' : 'Use of Information'}
                </h2>
                <p className="text-slate-600">
                  {language === 'es'
                    ? 'Utilizamos la información recopilada para proporcionar, mantener y mejorar nuestros servicios, procesar transacciones, enviar comunicaciones relacionadas con el servicio y cumplir con obligaciones legales.'
                    : 'We use the information collected to provide, maintain and improve our services, process transactions, send service-related communications and comply with legal obligations.'}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  3. {language === 'es' ? 'Compartición de Datos' : 'Data Sharing'}
                </h2>
                <p className="text-slate-600">
                  {language === 'es'
                    ? 'No vendemos tus datos personales. Compartimos información únicamente con: proveedores de servicios de pago (Stripe), autoridades fiscales (AEAT) cuando sea requerido legalmente, y proveedores de infraestructura técnica.'
                    : 'We do not sell your personal data. We share information only with: payment service providers (Stripe), tax authorities (AEAT) when legally required, and technical infrastructure providers.'}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  4. {language === 'es' ? 'Seguridad de Datos' : 'Data Security'}
                </h2>
                <p className="text-slate-600">
                  {language === 'es'
                    ? 'Implementamos medidas de seguridad técnicas y organizativas para proteger tus datos, incluyendo cifrado SSL/TLS, acceso restringido y copias de seguridad regulares.'
                    : 'We implement technical and organizational security measures to protect your data, including SSL/TLS encryption, restricted access and regular backups.'}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  5. {language === 'es' ? 'Tus Derechos (RGPD)' : 'Your Rights (GDPR)'}
                </h2>
                <p className="text-slate-600 mb-4">
                  {language === 'es'
                    ? 'Bajo el Reglamento General de Protección de Datos, tienes derecho a:'
                    : 'Under the General Data Protection Regulation, you have the right to:'}
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-2">
                  <li>{language === 'es' ? 'Acceder a tus datos personales' : 'Access your personal data'}</li>
                  <li>{language === 'es' ? 'Rectificar datos inexactos' : 'Rectify inaccurate data'}</li>
                  <li>{language === 'es' ? 'Solicitar la eliminación de tus datos' : 'Request deletion of your data'}</li>
                  <li>{language === 'es' ? 'Portabilidad de datos' : 'Data portability'}</li>
                  <li>{language === 'es' ? 'Oponerte al procesamiento' : 'Object to processing'}</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                  6. {language === 'es' ? 'Contacto' : 'Contact'}
                </h2>
                <p className="text-slate-600">
                  {language === 'es'
                    ? 'Para ejercer tus derechos o consultas sobre privacidad, contacta con nuestro Delegado de Protección de Datos en privacy@vendfactura.com'
                    : 'To exercise your rights or for privacy inquiries, contact our Data Protection Officer at privacy@vendfactura.com'}
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
